import { useLocation, Link, Navigate } from "react-router-dom";
import { CheckCircle2, Navigation, Clock, MapPin, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { CtaBand } from "@/components/common/CtaBand";
import { BeforeAfterGallery } from "@/components/common/BeforeAfterGallery";
import { FaqSection } from "@/components/home/FaqSection";
import { Seo } from "@/components/common/Seo";
import { buildLocalBusinessSchema, buildBreadcrumbSchema, buildFaqSchema } from "@/lib/schema";
import { LOCATIONS, LOCATION_CONTENT, TREATMENTS, CONTACT } from "@/data/site";
import { CtaPair } from "@/components/common/CtaButtons";
import { ToothIcon } from "@/components/common/ToothIcon";

export default function LocationDetail() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "");
  const l = LOCATIONS.find((x) => x.slug === slug);
  if (!l) return <Navigate to="/locations" replace />;

  const c = LOCATION_CONTENT[slug] || {};
  const faqs = c.faqs || [];
  const intro = c.intro || [
    `Patients from ${l.area} choose Dr. Ankesh Seth for gentle, ethical and modern dentistry. Whether you need a routine cleaning, a root canal or a complete smile makeover, our Paharia clinic is easy to reach and ready to help.`,
  ];
  const popular = (c.popularTreatments || [])
    .map((s) => TREATMENTS.find((t) => t.slug === s))
    .filter(Boolean);

  const reasons = [
    `Trusted, painless dental care within easy reach of ${l.area}`,
    "Same-day and emergency appointments available",
    "Modern equipment and hospital-grade sterilization",
    "Transparent pricing with clear treatment plans",
  ];

  return (
    <div data-testid="location-detail-page">
      <Seo
        title={`Dentist in ${l.area}, Varanasi | Dr. Ankesh Seth`}
        description={`Looking for a trusted dentist near ${l.area}? Dr. Ankesh Seth's Paharia clinic offers painless dental implants, RCT, smile makeover and full mouth rehabilitation for patients across ${l.area}, Varanasi.`}
        path={`/${l.slug}`}
        jsonLd={[
          buildLocalBusinessSchema(),
          buildBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Locations", path: "/locations" },
            { name: `Dentist in ${l.area}`, path: `/${l.slug}` },
          ]),
          ...(faqs.length ? [buildFaqSchema(faqs)] : []),
        ]}
      />
      <PageHero
        eyebrow="Location"
        titleLines={[`Dentist in`, l.area]}
        subtitle={`Advanced & painless dental care serving ${l.area}, Varanasi — ${l.distance === "Clinic location" ? "right in your neighbourhood" : `just ${l.distance} away`} at our Paharia clinic.`}
        crumbs={[{ label: "Locations", to: "/locations" }, { label: l.area }]}
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-6 py-24 md:px-12 md:py-28 lg:grid-cols-2 lg:gap-16 lg:px-20">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold text-brand-dark md:text-4xl">
              Your dental clinic near <span className="italic text-brand-blue">{l.area}</span>
            </h2>
            {intro.map((para, i) => (
              <p key={i} className="mt-5 text-lg leading-relaxed text-brand-muted">{para}</p>
            ))}
            <ul className="mt-8 space-y-3">
              {reasons.map((r) => (
                <li key={r} className="flex items-start gap-3 text-brand-dark">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-green" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <CtaPair />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-[2rem] border border-brand-line">
              <iframe
                title={`Directions from ${l.area}`}
                src={CONTACT.mapEmbed}
                className="h-72 w-full border-0 md:h-80"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="bg-brand-soft p-6">
                <p className="font-medium text-brand-dark">{CONTACT.address}</p>
                <div className="mt-3 flex flex-col gap-2 text-sm text-brand-muted">
                  <span className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-brand-blue" />
                    {CONTACT.timings[0].day}: {CONTACT.timings[0].hours}
                  </span>
                  <a
                    href={CONTACT.gmbUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="location-directions-link"
                    className="flex items-center gap-2 font-semibold text-brand-blue hover:underline"
                  >
                    <Navigation className="h-4 w-4" /> Get directions
                  </a>
                </div>
              </div>
            </div>

            {/* How to reach + nearby areas */}
            {(c.landmarks || c.nearby) && (
              <div className="mt-6 rounded-[2rem] border border-brand-line bg-white p-7">
                {c.landmarks && (
                  <>
                    <h3 className="flex items-center gap-2 font-display text-xl font-medium text-brand-dark">
                      <MapPin className="h-5 w-5 text-brand-blue" />
                      How to reach us from {l.area}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-brand-muted">{c.landmarks}</p>
                  </>
                )}
                {c.nearby && (
                  <>
                    <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-brand-green">
                      Also serving nearby
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {c.nearby.map((n) => (
                        <span key={n} className="rounded-full border border-brand-line bg-brand-soft px-3 py-1 text-xs font-medium text-brand-dark">
                          {n}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {/* Popular treatments for this area */}
      {popular.length > 0 && (
        <section className="bg-brand-soft">
          <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-12 lg:px-20">
            <Reveal className="mb-10 max-w-2xl">
              <h2 className="font-display text-3xl font-semibold text-brand-dark md:text-4xl">
                Popular treatments for {l.area} patients
              </h2>
              <p className="mt-3 text-brand-muted">
                The treatments {l.area} residents visit us for most — all delivered painlessly with
                modern technology and transparent pricing.
              </p>
            </Reveal>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {popular.map((t, i) => (
                <Reveal key={t.slug} delay={i * 0.07}>
                  <Link
                    to={`/${t.slug}`}
                    data-testid={`location-treatment-${t.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-brand-line bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand-blue/40 hover:shadow-[0_30px_60px_-35px_rgba(2,125,199,0.5)]"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-blue/10 text-brand-blue">
                      <ToothIcon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-4 font-display text-lg font-medium text-brand-dark">{t.name}</h3>
                    <p className="mt-2 flex-1 text-sm text-brand-muted">{t.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand-blue">
                      Learn more
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {faqs.length > 0 ? <FaqSection faqs={faqs} /> : <FaqSection muted />}
      <BeforeAfterGallery muted />
      <CtaBand title={`Best dentist near ${l.area}?`} />
    </div>
  );
}
